/**
 * ARCompletionPhotosTab - Before/after photo evidence for completed architectural requests.
 * Homeowners or inspectors upload photos to prove the approved work was done correctly.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Paper, Button, Grid, Chip, CircularProgress,
  Alert, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip,
} from "@mui/material";
import PhotoCameraIcon  from "@mui/icons-material/PhotoCamera";
import DeleteIcon       from "@mui/icons-material/Delete";
import ZoomInIcon       from "@mui/icons-material/ZoomIn";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import UploadFileIcon   from "@mui/icons-material/UploadFile";
import api, { API_BASE_URL } from "../../utils/api";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "";

const PhotoCard = ({ file, label, color, onDelete, onZoom }) => (
  <Paper elevation={0} sx={{
    borderRadius:"12px", border:`2px solid ${color}30`, overflow:"hidden", position:"relative",
    "&:hover .photo-actions":{ opacity:1 },
  }}>
    <Box sx={{ position:"absolute", top:8, left:8, zIndex:2 }}>
      <Chip size="small" label={label} sx={{ fontWeight:700, fontSize:10, bgcolor:color, color:"white", borderRadius:"6px" }} />
    </Box>
    <Box className="photo-actions" sx={{
      position:"absolute", top:8, right:8, zIndex:2, display:"flex", gap:0.5,
      opacity:0, transition:"opacity 0.2s",
    }}>
      <Tooltip title="View full size">
        <IconButton size="small" onClick={() => onZoom(file)}
          sx={{ bgcolor:"rgba(0,0,0,0.55)", color:"white", "&:hover":{ bgcolor:"rgba(0,0,0,0.8)" } }}>
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => onDelete(file.id)}
          sx={{ bgcolor:"rgba(220,38,38,0.7)", color:"white", "&:hover":{ bgcolor:"rgba(220,38,38,0.9)" } }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
    <Box component="img"
      src={`${API_BASE_URL}/request-images/${encodeURIComponent(file.file_name)}`}
      alt={file.label || label}
      sx={{ width:"100%", height:200, objectFit:"cover", display:"block" }}
      onError={e => { e.target.style.display="none"; }}
    />
    <Box sx={{ px:1.5, py:1, bgcolor:"rgba(255,255,255,0.95)" }}>
      <Typography variant="caption" sx={{ color:"#6b778c", display:"block" }}>
        {file.label || file.file_name} &bull; {fmtDate(file.uploaded_at || file.created_at)}
      </Typography>
    </Box>
  </Paper>
);

const ARCompletionPhotosTab = ({ requestId, requestDetails }) => {
  const [photos,    setPhotos]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snack,     setSnack]     = useState({ msg:"", sev:"success" });
  const [zoomFile,  setZoomFile]  = useState(null);
  const [uploadDlg, setUploadDlg] = useState(null); // "before" | "after" | "inspection"
  const [caption,   setCaption]   = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/request-files/${requestId}`);
      const all = res.data || [];
      // Completion photos are tagged with category "before", "after", or "inspection"
      setPhotos(all.filter(f =>
        f.category === "before" || f.category === "after" ||
        f.category === "inspection" || f.category === "completion"
      ));
    } catch { setPhotos([]); }
    finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("request_id", requestId);
      fd.append("category", uploadDlg);
      fd.append("label", caption || `${uploadDlg} photo`);
      await api.post("/request-files", fd, { headers:{ "Content-Type":"multipart/form-data" } });
      setSnack({ msg:`${uploadDlg} photo uploaded.`, sev:"success" });
      setUploadDlg(null); setCaption("");
      load();
    } catch { setSnack({ msg:"Upload failed.", sev:"error" }); }
    finally { setUploading(false); if(fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/request-files/${fileId}`);
      setSnack({ msg:"Photo deleted.", sev:"success" }); load();
    } catch { setSnack({ msg:"Delete failed.", sev:"error" }); }
  };

  const before     = photos.filter(p => p.category === "before");
  const after      = photos.filter(p => p.category === "after");
  const inspection = photos.filter(p => p.category === "inspection" || p.category === "completion");

  const btnSx = (color) => ({
    borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none",
    bgcolor:color, color:"white", "&:hover":{ bgcolor:color, filter:"brightness(0.9)", boxShadow:"none" },
  });

  return (
    <Box>
      {snack.msg && (
        <Alert severity={snack.sev} onClose={() => setSnack({ msg:"", sev:"success" })} sx={{ mb:2, borderRadius:2 }}>
          {snack.msg}
        </Alert>
      )}

      {/* Header + Upload buttons */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3, flexWrap:"wrap", gap:2 }}>
        <Box>
          <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.5 }}>
            <CompareArrowsIcon sx={{ color:"#2f63ad" }} />
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Completion Photo Evidence</Typography>
          </Box>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Upload before/after photos to document the work was completed per the approved plans.
          </Typography>
        </Box>
        <Box sx={{ display:"flex", gap:1, flexWrap:"wrap" }}>
          <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadDlg("before")} sx={btnSx("#374151")}>Before</Button>
          <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadDlg("after")}  sx={btnSx("#16a34a")}>After</Button>
          <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadDlg("inspection")} sx={btnSx("#2f63ad")}>Inspection</Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display:"flex", justifyContent:"center", py:6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Summary bar */}
          <Box sx={{ display:"flex", gap:2, mb:3, flexWrap:"wrap" }}>
            {[
              { label:"Before",     count:before.length,     color:"#374151" },
              { label:"After",      count:after.length,      color:"#16a34a" },
              { label:"Inspection", count:inspection.length, color:"#2f63ad" },
            ].map(({ label, count, color }) => (
              <Paper key={label} elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", px:2, py:1, display:"flex", alignItems:"center", gap:1 }}>
                <Typography variant="h6" sx={{ fontWeight:800, color }}>{count}</Typography>
                <Typography variant="caption" sx={{ color:"#6b778c" }}>{label} photos</Typography>
              </Paper>
            ))}
            {after.length > 0 && before.length > 0 && (
              <Chip icon={<CheckCircleIcon />} label="Before & After documented"
                sx={{ fontWeight:700, bgcolor:"rgba(22,163,74,0.1)", color:"#16a34a", borderRadius:"10px" }} />
            )}
          </Box>

          {/* Photo grids */}
          {[
            { label:"Before",     color:"#374151", items:before },
            { label:"After",      color:"#16a34a", items:after },
            { label:"Inspection", color:"#2f63ad", items:inspection },
          ].map(({ label, color, items }) => (
            <Box key={label} sx={{ mb:3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color, mb:1.5 }}>{label} Photos ({items.length})</Typography>
              {items.length === 0 ? (
                <Paper elevation={0} sx={{ borderRadius:"12px", border:`2px dashed ${color}30`, p:3, textAlign:"center" }}>
                  <PhotoCameraIcon sx={{ fontSize:32, color:`${color}60`, mb:0.5 }} />
                  <Typography variant="body2" sx={{ color:"#9aa5b4" }}>No {label.toLowerCase()} photos yet.</Typography>
                  <Button size="small" onClick={() => setUploadDlg(label.toLowerCase())}
                    sx={{ mt:1, borderRadius:"8px", textTransform:"none", color, fontWeight:600 }}>
                    Upload {label} Photo
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {items.map(f => (
                    <Grid item xs={12} sm={6} md={4} key={f.id}>
                      <PhotoCard file={f} label={label} color={color}
                        onDelete={handleDelete} onZoom={setZoomFile} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ))}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={!!uploadDlg} onClose={() => setUploadDlg(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>
          Upload {uploadDlg ? uploadDlg.charAt(0).toUpperCase()+uploadDlg.slice(1) : ""} Photo
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Caption (optional)" value={caption}
            onChange={e => setCaption(e.target.value)} sx={{ mb:2, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:"none" }} />
          <Button fullWidth variant="outlined" startIcon={<UploadFileIcon />}
            onClick={() => fileRef.current?.click()} disabled={uploading}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, py:2, borderStyle:"dashed" }}>
            {uploading ? "Uploading…" : "Click to choose photo"}
          </Button>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3 }}>
          <Button onClick={() => setUploadDlg(null)} variant="outlined" sx={{ borderRadius:"10px", textTransform:"none" }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Zoom Dialog */}
      <Dialog open={!!zoomFile} onClose={() => setZoomFile(null)} maxWidth="lg" fullWidth PaperProps={{ sx:{ borderRadius:"16px", bgcolor:"#000" } }}>
        <DialogTitle sx={{ color:"white", fontWeight:700 }}>{zoomFile?.label || zoomFile?.file_name}</DialogTitle>
        <DialogContent sx={{ p:0 }}>
          {zoomFile && (
            <Box component="img"
              src={`${API_BASE_URL}/request-images/${encodeURIComponent(zoomFile.file_name)}`}
              alt={zoomFile.label}
              sx={{ width:"100%", maxHeight:"80vh", objectFit:"contain", display:"block" }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor:"#111", px:3, py:2 }}>
          <Button onClick={() => setZoomFile(null)} variant="outlined" sx={{ borderRadius:"10px", textTransform:"none", color:"white", borderColor:"rgba(255,255,255,0.3)" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ARCompletionPhotosTab;
